import { useEffect, useState, useCallback } from 'react'
import {
  Users,
  Shield,
  Search,
  Plus,
  RotateCcw,
  KeyRound,
  Unlock,
  LogOut,
  CheckCircle2,
  AlertCircle,
  Clock,
  UserCheck,
  UserX,
} from 'lucide-react'
import { useCan } from '@/features/auth/AuthContext'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import {
  listAdminUsers,
  createAdminUser,
  updateAdminUser,
  resetAdminUserPassword,
  revokeAdminUserSessions,
  unlockAdminUser,
  listAuthEvents,
  listAdminRoles,
  type RoleDto,
  type AdminUserDto,
  type AuthEventDto,
} from '@/services/admin/adminClient'

export const DEFAULT_OFFICIAL_ROLES: RoleDto[] = [
  { code: 'ADMIN', name: 'Quản trị' },
  { code: 'APPROVER', name: 'Duyệt (Giám đốc)' },
  { code: 'PURCHASING', name: 'Mua hàng' },
  { code: 'WAREHOUSE', name: 'Kho' },
  { code: 'PLANNING', name: 'Kế hoạch' },
  { code: 'ENGINEERING', name: 'Kỹ thuật' },
  { code: 'SALE', name: 'Sale' },
]

export function AdminUsersPage() {
  const canAdminUsers = useCan('admin.users')

  const [activeTab, setActiveTab] = useState<'users' | 'events'>('users')
  const [users, setUsers] = useState<AdminUserDto[]>([])
  const [events, setEvents] = useState<AuthEventDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'disabled'>('')

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUserDto | null>(null)
  const [resettingUser, setResettingUser] = useState<AdminUserDto | null>(null)

  // Form states
  const [createUsername, setCreateUsername] = useState('')
  const [createDisplayName, setCreateDisplayName] = useState('')
  const [createPassword, setCreatePassword] = useState('')
  const [createRoles, setCreateRoles] = useState<string[]>(['PURCHASING'])

  const [editDisplayName, setEditDisplayName] = useState('')
  const [editRoles, setEditRoles] = useState<string[]>([])

  const [newPassword, setNewPassword] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<RoleDto[]>([])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showCreateModal) setShowCreateModal(false)
        if (editingUser) setEditingUser(null)
        if (resettingUser) setResettingUser(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showCreateModal, editingUser, resettingUser])

  useEffect(() => {
    if (!canAdminUsers) return
    void listAdminRoles()
      .then((roles) => {
        if (roles && roles.length > 0) {
          setAvailableRoles(
            roles.map((r) => ({
              code: r.code,
              name: r.code === 'APPROVER' ? 'Duyệt (Giám đốc)' : r.name,
            })),
          )
        }
      })
      .catch((err) => {
        console.error('Failed to load roles:', err)
      })
  }, [canAdminUsers])

  const rolesToDisplay = availableRoles.length > 0 ? availableRoles : DEFAULT_OFFICIAL_ROLES

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg)
    setTimeout(() => setSuccessMsg(null), 4000)
  }

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listAdminUsers({
        search: search.trim() || undefined,
        status: statusFilter || undefined,
      })
      setUsers(data)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải danh sách người dùng')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  const loadEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listAuthEvents(100)
      setEvents(data)
    } catch (err: any) {
      setError(err?.message || 'Không thể tải nhật ký xác thực')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!canAdminUsers) return
    if (activeTab === 'users') {
      void loadUsers()
    } else {
      void loadEvents()
    }
  }, [canAdminUsers, activeTab, loadUsers, loadEvents])

  if (!canAdminUsers) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-3 text-center">
        <Shield className="h-12 w-12 text-slate-300" />
        <div className="text-lg font-medium text-slate-700">Truy cập bị từ chối</div>
        <div className="max-w-md text-sm text-slate-500">
          Bạn không có quyền quản trị người dùng (`admin.users`). Vui lòng liên hệ quản trị viên hệ thống nếu cần cấp quyền.
        </div>
      </div>
    )
  }

  // Handle create user
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createUsername.trim() || !createDisplayName.trim() || !createPassword.trim()) {
      setError('Vui lòng điền đầy đủ thông tin')
      return
    }
    setActionLoading(true)
    setError(null)
    try {
      await createAdminUser({
        username: createUsername.trim().toLowerCase(),
        display_name: createDisplayName.trim(),
        password: createPassword,
        roles: createRoles,
      })
      setShowCreateModal(false)
      setCreateUsername('')
      setCreateDisplayName('')
      setCreatePassword('')
      setCreateRoles(['PURCHASING'])
      showSuccess('Tạo người dùng thành công')
      void loadUsers()
    } catch (err: any) {
      setError(err?.message || 'Lỗi khi tạo người dùng')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle edit user
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return
    setActionLoading(true)
    setError(null)
    try {
      await updateAdminUser(editingUser.id, {
        display_name: editDisplayName.trim(),
        roles: editRoles,
      })
      setEditingUser(null)
      showSuccess(`Cập nhật người dùng "${editingUser.username}" thành công`)
      void loadUsers()
    } catch (err: any) {
      setError(err?.message || 'Lỗi khi cập nhật người dùng')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle status toggle
  const handleToggleStatus = async (user: AdminUserDto) => {
    const nextStatus = user.status === 'active' ? 'disabled' : 'active'
    const confirmMsg =
      nextStatus === 'disabled'
        ? `Bạn có chắc chắn muốn vô hiệu hóa tài khoản "${user.username}"? Người dùng này sẽ bị thu hồi toàn bộ phiên đăng nhập.`
        : `Kích hoạt lại tài khoản "${user.username}"?`
    if (!window.confirm(confirmMsg)) return

    try {
      await updateAdminUser(user.id, { status: nextStatus })
      showSuccess(`Đã ${nextStatus === 'active' ? 'kích hoạt' : 'vô hiệu hóa'} tài khoản "${user.username}"`)
      void loadUsers()
    } catch (err: any) {
      alert(err?.message || 'Không thể thay đổi trạng thái người dùng')
    }
  }

  // Handle unlock user
  const handleUnlock = async (user: AdminUserDto) => {
    try {
      await unlockAdminUser(user.id)
      showSuccess(`Đã mở khóa tài khoản "${user.username}" thành công`)
      void loadUsers()
    } catch (err: any) {
      alert(err?.message || 'Không thể mở khóa tài khoản')
    }
  }

  // Handle revoke sessions
  const handleRevokeSessions = async (user: AdminUserDto) => {
    if (!window.confirm(`Đăng xuất tất cả các thiết bị đang đăng nhập bằng tài khoản "${user.username}"?`)) return
    try {
      await revokeAdminUserSessions(user.id)
      showSuccess(`Đã thu hồi tất cả phiên đăng nhập của "${user.username}"`)
    } catch (err: any) {
      alert(err?.message || 'Lỗi khi thu hồi phiên')
    }
  }

  // Handle reset password submit
  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resettingUser || !newPassword.trim()) return
    setActionLoading(true)
    setError(null)
    try {
      await resetAdminUserPassword(resettingUser.id, newPassword)
      setResettingUser(null)
      setNewPassword('')
      showSuccess(`Đặt lại mật khẩu thành công cho "${resettingUser.username}". Người dùng buộc đổi mật khẩu ở lần đăng nhập tiếp theo.`)
      void loadUsers()
    } catch (err: any) {
      setError(err?.message || 'Lỗi khi đặt lại mật khẩu')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Hệ thống', to: '/admin/users' },
          { label: 'Quản trị Người dùng & Quyền' },
        ]}
      />

      <PageHeader
        title="Quản trị Người dùng & Quyền hạn (RBAC)"
        subtitle="Quản lý tài khoản cán bộ công nhân viên, phân quyền vai trò và giám sát nhật ký xác thực an toàn."
      />

      {successMsg && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <AlertCircle className="h-5 w-5 shrink-0 text-rose-600" />
          <span>{error}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'border-emerald-800 text-emerald-900 font-semibold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Users className="h-4 w-4" />
          Danh sách Người dùng ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'events'
              ? 'border-emerald-800 text-emerald-900 font-semibold'
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Clock className="h-4 w-4" />
          Nhật ký Xác thực & Bảo mật ({events.length})
        </button>
      </div>

      {activeTab === 'users' ? (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Tìm username hoặc họ tên..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-full sm:w-64 rounded-md border border-slate-200 pl-8 pr-3 text-sm outline-none focus:border-emerald-700"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-700"
              >
                <option value="">Tất cả trạng thái</option>
                <option value="active">Đang hoạt động</option>
                <option value="disabled">Đã vô hiệu hóa</option>
              </select>

              <Button variant="ghost" size="sm" onClick={() => void loadUsers()} disabled={loading}>
                <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Làm mới
              </Button>
            </div>

            <Button
              size="sm"
              onClick={() => {
                setError(null)
                setShowCreateModal(true)
              }}
              className="bg-emerald-800 text-white hover:bg-emerald-900"
            >
              <Plus className="h-4 w-4 mr-1" />
              Thêm người dùng mới
            </Button>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm text-slate-700">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="w-12 px-4 py-3 text-center font-semibold">STT</th>
                  <th className="px-4 py-3 font-semibold">Tài khoản & Họ tên</th>
                  <th className="px-4 py-3 font-semibold">Vai trò (Roles)</th>
                  <th className="px-4 py-3 font-semibold">Trạng thái</th>
                  <th className="px-4 py-3 font-semibold">Đổi MK lần đầu</th>
                  <th className="px-4 py-3 font-semibold">Đăng nhập gần nhất</th>
                  <th className="px-4 py-3 text-right font-semibold">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">
                      Đang tải danh sách người dùng...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">
                      Không tìm thấy người dùng nào phù hợp.
                    </td>
                  </tr>
                ) : (
                  users.map((u, index) => (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-center tabular-nums text-slate-500">{index + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{u.display_name}</div>
                        <div className="font-mono text-xs text-slate-500">@{u.username}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <span
                              key={r}
                              className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                                r === 'ADMIN'
                                  ? 'bg-purple-100 text-purple-800'
                                  : r === 'APPROVER'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {u.status === 'active' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
                            <UserCheck className="h-3 w-3" /> Hoạt động
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700 border border-rose-200">
                            <UserX className="h-3 w-3" /> Vô hiệu hóa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {u.must_change_password ? (
                          <span className="text-amber-600 font-medium">Bắt buộc</span>
                        ) : (
                          <span className="text-slate-400">Đã đổi</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {u.last_login_at
                          ? new Date(u.last_login_at).toLocaleString('vi-VN')
                          : 'Chưa từng đăng nhập'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Sửa thông tin và vai trò"
                            onClick={() => {
                              setEditingUser(u)
                              setEditDisplayName(u.display_name)
                              setEditRoles([...u.roles])
                              setError(null)
                            }}
                          >
                            Sửa
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            title="Đặt lại mật khẩu tạm"
                            onClick={() => {
                              setResettingUser(u)
                              setNewPassword('')
                              setError(null)
                            }}
                          >
                            <KeyRound className="h-3.5 w-3.5 mr-1" />
                            Reset MK
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            title="Mở khóa tài khoản nếu bị khóa tạm thời do gõ sai"
                            onClick={() => void handleUnlock(u)}
                          >
                            <Unlock className="h-3.5 w-3.5 mr-1" />
                            Mở khóa
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            title={u.status === 'active' ? 'Vô hiệu hóa tài khoản' : 'Kích hoạt tài khoản'}
                            onClick={() => void handleToggleStatus(u)}
                            className={u.status === 'active' ? 'text-rose-600 hover:text-rose-700' : 'text-emerald-700'}
                          >
                            {u.status === 'active' ? 'Khóa' : 'Kích hoạt'}
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            title="Đăng xuất khỏi mọi thiết bị"
                            onClick={() => void handleRevokeSessions(u)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            <LogOut className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Events Tab */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Hiển thị 100 sự kiện xác thực gần nhất (đăng nhập thành công, thất bại, đổi mật khẩu, quản trị).
            </div>
            <Button variant="ghost" size="sm" onClick={() => void loadEvents()} disabled={loading}>
              <RotateCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Làm mới nhật ký
            </Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm text-slate-700">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 font-semibold">Thời gian</th>
                  <th className="px-4 py-3 font-semibold">Tài khoản</th>
                  <th className="px-4 py-3 font-semibold">Sự kiện</th>
                  <th className="px-4 py-3 font-semibold">Địa chỉ IP</th>
                  <th className="px-4 py-3 font-semibold">Chi tiết</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-500">
                      Chưa có sự kiện xác thực nào được ghi nhận.
                    </td>
                  </tr>
                ) : (
                  events.map((ev) => (
                    <tr key={ev.id} className="hover:bg-slate-50 font-mono text-xs">
                      <td className="px-4 py-2.5 text-slate-500">
                        {new Date(ev.at).toLocaleString('vi-VN')}
                      </td>
                      <td className="px-4 py-2.5 font-semibold text-slate-800">
                        {ev.username}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                            ev.event === 'LOGIN_OK'
                              ? 'bg-emerald-100 text-emerald-800'
                              : ev.event === 'LOGIN_FAIL'
                              ? 'bg-rose-100 text-rose-800'
                              : ev.event === 'ACCOUNT_LOCKED'
                              ? 'bg-amber-100 text-amber-900'
                              : ev.event === 'ACCOUNT_UNLOCKED'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {ev.event}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{ev.ip || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-500 max-w-xs truncate">
                        {ev.detail ? JSON.stringify(ev.detail) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Create User */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Tạo người dùng mới</h3>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Tên đăng nhập (username) *</label>
                <input
                  type="text"
                  required
                  pattern="[a-zA-Z0-9_\-\.]{3,64}"
                  title="3 đến 64 ký tự chữ cái, chữ số, gạch dưới"
                  value={createUsername}
                  onChange={(e) => setCreateUsername(e.target.value)}
                  placeholder="VD: buyer_01"
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Họ và tên hiển thị *</label>
                <input
                  type="text"
                  required
                  value={createDisplayName}
                  onChange={(e) => setCreateDisplayName(e.target.value)}
                  placeholder="VD: Nguyễn Văn A"
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Mật khẩu tạm thời *</label>
                <input
                  type="text"
                  required
                  minLength={8}
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder="Tối thiểu 8 ký tự, có số và chữ"
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono outline-none focus:border-emerald-700"
                />
                <p className="mt-1 text-xs text-slate-500">Người dùng sẽ bắt buộc phải đổi mật khẩu ở lần đăng nhập đầu tiên.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Phân vai trò (Roles) *</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                  {rolesToDisplay.map((role) => (
                    <label key={role.code} className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createRoles.includes(role.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCreateRoles([...createRoles, role.code])
                          } else {
                            setCreateRoles(createRoles.filter((r) => r !== role.code))
                          }
                        }}
                        className="rounded border-slate-300 text-emerald-800 focus:ring-emerald-700"
                      />
                      <span>{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setShowCreateModal(false)} disabled={actionLoading}>
                  Hủy
                </Button>
                <Button type="submit" disabled={actionLoading || createRoles.length === 0} className="bg-emerald-800 text-white hover:bg-emerald-900">
                  {actionLoading ? 'Đang tạo...' : 'Tạo tài khoản'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit User */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-4">
              Cập nhật người dùng: <span className="font-mono text-emerald-800">@{editingUser.username}</span>
            </h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Họ và tên hiển thị *</label>
                <input
                  type="text"
                  required
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-emerald-700"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Phân vai trò (Roles) *</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                  {rolesToDisplay.map((role) => (
                    <label key={role.code} className="flex items-center gap-2 text-xs text-slate-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editRoles.includes(role.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditRoles([...editRoles, role.code])
                          } else {
                            setEditRoles(editRoles.filter((r) => r !== role.code))
                          }
                        }}
                        className="rounded border-slate-300 text-emerald-800 focus:ring-emerald-700"
                      />
                      <span>{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setEditingUser(null)} disabled={actionLoading}>
                  Hủy
                </Button>
                <Button type="submit" disabled={actionLoading || editRoles.length === 0} className="bg-emerald-800 text-white hover:bg-emerald-900">
                  {actionLoading ? 'Đang lưu...' : 'Lưu thay đổi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Reset Password */}
      {resettingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-base font-semibold text-slate-900 mb-2">
              Đặt lại mật khẩu cho: <span className="font-mono text-emerald-800">@{resettingUser.username}</span>
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Nhập mật khẩu tạm mới cho người dùng. Toàn bộ các phiên đăng nhập hiện tại sẽ bị thu hồi và người dùng bắt buộc phải đổi mật khẩu ở lần đăng nhập tiếp theo.
            </p>
            <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Mật khẩu tạm mới *</label>
                <input
                  type="text"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Tối thiểu 8 ký tự, có số và chữ"
                  className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm font-mono outline-none focus:border-emerald-700"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" type="button" onClick={() => setResettingUser(null)} disabled={actionLoading}>
                  Hủy
                </Button>
                <Button type="submit" disabled={actionLoading || newPassword.length < 8} className="bg-emerald-800 text-white hover:bg-emerald-900">
                  {actionLoading ? 'Đang đặt lại...' : 'Xác nhận đặt lại'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
